import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getTenantSlug } from "../lib/tenant";
import { BACKEND_URL } from "../lib/backendUrl";
import { LoadingState } from "../components/EmptyState";

/**
 * Renders the tenant-scoped legal page (Privacy Policy or Terms of Service).
 *
 * The page is rendered inside an iframe sandboxed to the backend's HTMLResponse —
 * this keeps the styled template intact (with the tenant's name/contact details
 * already interpolated server-side) without requiring us to duplicate the
 * styling here or expose the raw HTML to React's reconciler.
 *
 * Routes:
 *   /legal/privacy   → uses getTenantSlug() to resolve the current tenant
 *   /legal/terms     → same
 *   /legal/:slug/privacy   → explicit tenant override (for deep links)
 *   /legal/:slug/terms     → same
 */
export default function LegalPage({ doc }) {
  const params = useParams();
  const slug = params.slug || getTenantSlug();
  const docType = doc || params.doc || "privacy";

  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const apiBase = BACKEND_URL || "";
  const src = `${apiBase}/api/legal/${encodeURIComponent(slug)}/${docType}`;

  useEffect(() => {
    // Pre-flight HEAD-like fetch so we can show a friendly 404 instead of a
    // blank iframe when the tenant doesn't exist.
    let cancelled = false;
    const preflight = async () => {
      try {
        const r = await fetch(src, { method: "GET" });
        if (cancelled) return;
        if (!r.ok) setError(`Could not load ${docType} (HTTP ${r.status}).`);
        setLoaded(true);
      } catch (e) {
        if (!cancelled) {
          setError(`Network error loading ${docType}.`);
          setLoaded(true);
        }
      }
    };
    preflight();
    return () => { cancelled = true; };
  }, [src, docType]);

  if (!loaded) {
    return <LoadingState fullHeight className="min-h-screen" testId="legal-page-loading" />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-8" data-testid="legal-page-error">
        <h1 className="text-xl font-bold text-neutral-700 mb-2">Document not available</h1>
        <p className="text-sm text-neutral-500 max-w-md">{error}</p>
        <a href="/" className="mt-6 text-brand-sky underline text-sm">Go back home</a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`legal-page-${docType}`}>
      <iframe
        src={src}
        title={`${docType} policy`}
        className="w-full h-screen border-0"
        sandbox="allow-same-origin"
      />
    </div>
  );
}
